import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes, { func, oneOf, string } from 'prop-types';
import { EventsConst, CandidateOpportunitiesConst } from '@axiom/const';
import {
  Button,
  CondensedLarge,
  CondensedMedium,
  ContextMenu,
  ContextMenuItem,
  EmailIcon,
  Flashy,
  Gutter,
  InPersonIcon,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  OtherActivityIcon,
  Paragraph,
  PhoneIcon,
  SectionHeader,
} from '@axiom/ui';

import { TalentActivitiesApi } from '../../api/talent-activities-api';
import { fullNameByIdSelector } from '../../redux/selectors/users';
import { DateUtil } from '../../utils/date-util';
import { eventShape } from '../../models/event';
import { EventsStore, EventSubjectConst } from '../../stores/events-store';
import { OpportunityCandidatesStore } from '../../stores/opportunity-candidates-store';
import RichTextEditor from '../RichTextEditor/RichTextEditor';

import {
  ALIListItem,
  ALIContent,
  ALIAuthor,
  ALIDate,
} from './ActivityLogItemStyles';

const { Types } = EventsConst;

const iconForType = {
  [Types.Call]: PhoneIcon,
  [Types.Email]: EmailIcon,
  [Types.Interview]: InPersonIcon,
  [Types.InPerson]: InPersonIcon,
  // TBD? [Types.Note]: NoteIcon,
  [Types.Video]: PhoneIcon,
};
const AFCActionPurposes = new Set([
  CandidateOpportunitiesConst.AllStatusLabels.ClientPassed,
  CandidateOpportunitiesConst.AllStatusLabels.InterviewRequested,
  CandidateOpportunitiesConst.AllStatusLabels.HireRequested,
  `${CandidateOpportunitiesConst.AllStatusLabels.InterviewRequested} - Zoom not supported`,
  'Interview Needs Attention',
]);

class ActivityLogItem extends Component {
  constructor(config) {
    super(config);
    this.state = {
      AFCActionDialogOpen: false,
      confirmDeleteModal: false,
    };
  }

  onAFCActionDialogOpen = () => {
    this.setState({ AFCActionDialogOpen: true });
  };

  onAFCActionDialogClose = () => {
    this.setState({ AFCActionDialogOpen: false });
  };

  toggleDetailsViewOrOpenPassModal = () => {
    const { event, onEditClick } = this.props;
    if (AFCActionPurposes.has(event.purpose)) {
      this.onAFCActionDialogOpen();
    } else {
      onEditClick(event);
    }
  };

  lookupLastAuthoredByName = () => {
    const {
      event,
      opportunityCandidatesState,
      usersById,
      candidateDisplayName,
    } = this.props;
    const author =
      usersById[event.lastAuthoredBy] ||
      opportunityCandidatesState?.data?.find(
        state => state.id === event.candidateId
      )?.calculatedDisplayName ||
      candidateDisplayName;
    return author;
  };

  deleteActivity = async () => {
    this.setState({
      confirmDeleteModal: false,
    });
    await EventsStore.delete(
      this.props.eventSubject,
      this.props.eventSubjectId,
      this.props.event.id
    );

    if (this.props.event.candidateId) {
      await TalentActivitiesApi.refreshActivities(this.props.event.candidateId);
    }
  };

  render() {
    const { event, onEditClick } = this.props;
    const { confirmDeleteModal, AFCActionDialogOpen } = this.state;
    const lastAuthoredBy = this.lookupLastAuthoredByName();
    const Icon = iconForType[event.type] || OtherActivityIcon;

    return (
      <>
        <ALIListItem data-test="ACTIVITYLOGITEM">
          <Layout position="middle">
            <LayoutItem fluid>
              <ALIContent
                onClick={this.toggleDetailsViewOrOpenPassModal}
                role="button"
                data-test="CONTENTBUTTON"
              >
                <div data-test="ICON" data-value={event.type}>
                  <Icon />
                </div>
                <CondensedLarge name="ACTIVITY_LOG_ITEM_PURPOSE">
                  <Flashy bold>{event.purpose}</Flashy>
                </CondensedLarge>
                <ALIAuthor
                  data-test="ACTIVITYLOGITEMAUTHOR"
                  data-value={event.lastAuthoredBy}
                >
                  <CondensedMedium as="span">{lastAuthoredBy}</CondensedMedium>
                </ALIAuthor>
                <ALIDate data-test="ACTIVITYLOGITEMDATE">
                  <CondensedMedium>
                    {DateUtil.displayDate(event.scheduledDate)}
                  </CondensedMedium>
                </ALIDate>
              </ALIContent>
            </LayoutItem>
            <ContextMenu direction="left">
              {!AFCActionPurposes.has(event.purpose) && (
                <ContextMenuItem onClick={() => onEditClick(event)}>
                  Edit
                </ContextMenuItem>
              )}
              <ContextMenuItem
                onClick={() => {
                  this.setState({
                    confirmDeleteModal: true,
                  });
                }}
              >
                Delete
              </ContextMenuItem>
            </ContextMenu>
          </Layout>
        </ALIListItem>
        {!!confirmDeleteModal && (
          <Modal size="confirm">
            <ModalHeader
              onClose={() => this.setState({ confirmDeleteModal: false })}
            >
              Confirm Deletion
            </ModalHeader>
            <ModalSection>
              <Paragraph>
                Are you sure you want to delete this {event.purpose} activity?
              </Paragraph>
            </ModalSection>
            <ModalFooter>
              <Button
                variation="outline"
                onClick={() => this.setState({ confirmDeleteModal: false })}
              >
                Cancel
              </Button>
              <Button onClick={this.deleteActivity}>Delete Activity</Button>
            </ModalFooter>
          </Modal>
        )}
        {!!AFCActionDialogOpen && (
          <Modal size="confirm">
            <ModalHeader onClose={this.onAFCActionDialogClose}>
              Activity
            </ModalHeader>
            <ModalSection>
              <Gutter bottom="24px">
                <SectionHeader>{event.purpose}</SectionHeader>
              </Gutter>
              <RichTextEditor
                name="description"
                id="description"
                initialValue={event.description}
                readOnly
              />
            </ModalSection>
            <ModalFooter>
              <Button onClick={this.onAFCActionDialogClose}>Close</Button>
            </ModalFooter>
          </Modal>
        )}
      </>
    );
  }
}

ActivityLogItem.defaultProps = {};

ActivityLogItem.propTypes = {
  candidateDisplayName: string,
  event: eventShape.isRequired,
  eventSubject: oneOf(Object.values(EventSubjectConst)).isRequired,
  eventSubjectId: string.isRequired,
  opportunityCandidatesState:
    OpportunityCandidatesStore.getStateShape().isRequired,
  usersById: PropTypes.shape({ id: PropTypes.string }).isRequired,
  onEditClick: func.isRequired,
};

const mapStateToProps = state => ({
  usersById: fullNameByIdSelector(state),
  opportunityCandidatesState: OpportunityCandidatesStore.select(state),
});

export default connect(mapStateToProps)(ActivityLogItem);
